import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BitmapText, Container, useApp } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import { Graphics } from 'pixi.js';
import gsap from 'gsap';

import { Spine } from '../../../core/classes/spine';
import { isMobile } from '../../../core/helpers/isMobile';
import { isoStandard } from '../../../core/helpers/isoStandard';
import { amountContainerInfo } from '../../../core/constants/amountContainerInfo';

import { gameStore } from '../../../store/gameStore';
import { winBarStore } from '../../../store/winBarStore';
import { winPopupStore } from '../../../store/winPopupStore';
import { userInfoStore } from '../../../store/userInfoStore';
import { applicationStore } from '../../../store/applicationStore';

import { gameDrawer } from '../../../services/gameDrawer';
import { gameService } from '../../../services/gameService';
import { soundService, SoundTypes } from '../../../services/soundService';
import { betReplayService } from '../../../services/betReplayService';

const winPopupTypes = {
  NONE: 0,
  BIG: 1,
  MEGA: 2,
  SUPER: 3,
  EPIC: 4,
  MAX: 5,
};

const winPopupAnimationEnum = [
  {
    type: winPopupTypes.BIG,
    duration: 3000,
    animationNames: ['bigStart', 'bigLoop'],
  },
  {
    type: winPopupTypes.MEGA,
    duration: 4000,
    animationNames: ['bigStart', 'bigLoop', 'megaChange', 'megaLoop'],
  },
  {
    type: winPopupTypes.SUPER,
    duration: 5000,
    animationNames: ['bigStart', 'bigLoop', 'megaChange', 'megaLoop', 'superChange', 'superLoop'],
  },
  {
    type: winPopupTypes.EPIC,
    duration: 12000,
    animationNames: [
      'bigStart',
      'bigLoop',
      'megaChange',
      'megaLoop',
      'superChange',
      'superLoop',
      'epicChange',
      'epicLoop',
    ],
  },
];
let timeLine;

const WinPopup = observer(({ containerConfig }) => {
  const [popupWinAmount, setPopupWinAmount] = useState('');
  const [winPopupSpine, setWinPopupSpine] = useState(null);
  const [isNumberAnimationEnd, setIsNumberAnimationEnd] = useState(false);
  const [isReplayEndCalled, setIsReplayEndCalled] = useState(false);

  const { winAmount } = winBarStore;
  const { precision } = userInfoStore;
  const { stage } = useApp();

  const {
    spinResult: { gambleInfo },
  } = gameStore;
  const winPopupRef = useRef();
  const spineRef = useRef();
  const bitmapRef = useRef();

  const {
    isOpenWinPopup,
    winPopupTypeOddValue,
    isFinishPopupAnimation,
    winPopupClose,
    setIsFinishPopupAnimation,
  } = winPopupStore;

  const winPopupType = useMemo(() => {
    if (winPopupTypeOddValue >= 201) return winPopupTypes.EPIC;
    if (winPopupTypeOddValue >= 101) return winPopupTypes.SUPER;
    if (winPopupTypeOddValue >= 51) return winPopupTypes.MEGA;
    if (winPopupTypeOddValue >= 15) return winPopupTypes.BIG;
    return winPopupTypes.NONE;
  }, [winPopupTypeOddValue]);

  const musicTypes = useMemo(() => {
    return {
      [winPopupTypes.BIG]: SoundTypes.BigWinSound,
      [winPopupTypes.MEGA]: SoundTypes.MegaWinSound,
      [winPopupTypes.SUPER]: SoundTypes.SuperWinSound,
      [winPopupTypes.EPIC]: SoundTypes.EpicWinSound,
      [winPopupTypes.NONE]: null,
    };
  }, []);

  const animationModel = useMemo(() => {
    return winPopupAnimationEnum.find((el) => el.type === winPopupType);
  }, [winPopupType]);

  const handleReplayEnd = useCallback(() => {
    if (isReplayEndCalled) return;

    setIsReplayEndCalled(true);
    if (userInfoStore.isReplay) {
      setTimeout(() => {
        betReplayService.sendReplayEventEnd();
      }, 1000);
    }
  }, [isReplayEndCalled]);

  useEffect(() => {
    if (!animationModel) return;
    const { duration } = animationModel;
    timeLine && timeLine.kill();
    timeLine = gsap.timeline();
    soundService.play(SoundTypes.AmountIncrease);
    timeLine.to(bitmapRef.current, {
      textContent: winAmount,
      duration: duration / 1000 - 1,
      ease: 'none',
      snap: { textContent: 0.01 },
      stagger: {
        each: 1,
        onUpdate: function () {
          setPopupWinAmount(isoStandard(this.targets()[0].textContent, true));
        },
        onComplete: function () {
          setPopupWinAmount(isoStandard(+winAmount, true));
          timeLine && timeLine.kill();
          setIsNumberAnimationEnd(true);
          handleReplayEnd();
        },
      },
    });
  }, [handleReplayEnd, animationModel, bitmapRef, gambleInfo, precision, winAmount]);

  const handleCompleteAnimation = useCallback(
    (evt, spine, animationName) => {
      if (!animationModel) return;
      const { animationNames } = animationModel;
      const index = animationNames.findIndex((el) => el === animationName);
      if (index + 1 < animationNames.length) {
        spine.play(animationNames[index + 1]);
        spine.addListener(
          'complete',
          (e) => {
            handleCompleteAnimation(e, spine, animationNames[index + 1]);
          },
          null,
          animationNames[index + 1]
        );
      } else {
        setIsFinishPopupAnimation(true);
        spine.play(animationNames[animationNames.length - 1], true);
        spine.addListener(
          'complete',
          () => {
            gameService.handleAutoSpin({ gambleInfo, winAmount });
          },
          null,
          animationNames[animationNames.length - 1]
        );
      }
    },
    [animationModel, setIsFinishPopupAnimation, gambleInfo, winAmount]
  );

  const handleFinishPopupAnimation = useCallback(
    (e) => {
      e.stopPropagation();
      if (!animationModel) return;
      const { animationNames } = animationModel;
      if (winPopupRef.current && (!isFinishPopupAnimation || !isNumberAnimationEnd)) {
        timeLine.progress(1).pause();
        setPopupWinAmount(isoStandard(winBarStore.winAmount, true));
        spineRef.current.children[0].play(animationNames[animationNames.length - 1], true);
        setIsFinishPopupAnimation(true);
        setIsNumberAnimationEnd(true);
        gameService.handleAutoSpin({ gambleInfo, winAmount });
        handleReplayEnd();
      } else {
        handleReplayEnd();
        winPopupClose();
        applicationStore.setIsShowMobileBigWinLayer(false);
        winBarStore.updateShowAmountContainerInfo(amountContainerInfo.winContainer, true);
        gameDrawer.animateWinAmount({ value: winBarStore.winAmount, duration: 0 });
        setPopupWinAmount('0');
        spineRef.current.children[0].stop();
        spineRef.current.children.length = 0;
        setIsFinishPopupAnimation(false);
        setIsNumberAnimationEnd(false);
        gameService.handleAutoSpin({ gambleInfo, winAmount });
      }
    },
    [
      winAmount,
      gambleInfo,
      animationModel,
      isNumberAnimationEnd,
      isFinishPopupAnimation,
      winPopupClose,
      setIsNumberAnimationEnd,
      setIsFinishPopupAnimation,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        handleFinishPopupAnimation(e);
      }
    },
    [handleFinishPopupAnimation]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    stage.addEventListener('pointerup', handleFinishPopupAnimation, true);
    return () => {
      stage.removeEventListener('pointerup', handleFinishPopupAnimation, true);
    };
  }, [handleFinishPopupAnimation, stage]);

  useEffect(() => {
    return () => {
      timeLine.kill();
      soundService.stopAllSounds();
    };
  }, []);

  useEffect(() => {
    if (!isMobile()) {
      const graphics = new Graphics();
      graphics.beginFill('black');
      graphics.alpha = 0.5;
      graphics.drawRect(0, 0, isMobile() ? 540 : 1440, isMobile() ? 960 : 860);
      winPopupRef.current.addChildAt(graphics, 0);
    }
  }, []);

  useEffect(() => {
    if (!animationModel || !winPopupSpine) return;
    const animName = animationModel.animationNames[0];
    soundService.play(musicTypes[animationModel.type]);
    winPopupSpine.play(animName, false);
    winPopupSpine.addListener(
      'complete',
      (e) => {
        handleCompleteAnimation(e, winPopupSpine, animName);
        winPopupSpine.removeAllListeners();
      },
      null,
      animName
    );
  }, [animationModel, handleCompleteAnimation, musicTypes, winPopupSpine]);

  useEffect(() => {
    const spine = new Spine({ key: 'winPopup' });
    spineRef.current.addChild(spine);
    setWinPopupSpine(spine);
    spine.addChildToSlot('num', bitmapRef.current);
  }, [setWinPopupSpine, spineRef]);

  return (
    <Container cursor='pointer' ref={winPopupRef} visible={isOpenWinPopup} name='WinPopupContainer'>
      <Container ref={spineRef} x={containerConfig.x} y={containerConfig.y} />
      <BitmapText
        y={isMobile() ? 130 : 190}
        anchor={0.5}
        resolution={2}
        ref={bitmapRef}
        text={popupWinAmount}
        style={{ fontName: 'winPopupNumbers' }}
        fontSize={isMobile() && popupWinAmount.length > 8 ? 20 : isMobile() ? 30 : 80}
      />
    </Container>
  );
});

export default WinPopup;
